<script lang="ts" setup>
import {
  Component,
  computed,
  getCurrentInstance,
  nextTick,
  onMounted,
  onUnmounted,
  provide,
  ref,
  watch,
} from "vue";
import { useEvent } from "vue-composable";
import { dispatch } from "@/utils/bus";
import { useMenuPosition } from "@/composables/useMenuPosition";

const props = defineProps<{
  modelValue?: boolean;
  noAutoOpen?: boolean;
  target?: Record<string, any> | HTMLElement | string | Component | null;
  renderTarget?: string | HTMLElement | Component | null;
  offset?: [left: number, top: number];
  maxHeight?: number;
  id?: string;
  /**
   * Keep the menu open regardless of mouseleave / scroll on the
   * surrounding parent. Useful for menus driven by keyboard focus +
   * trigger char (e.g. "/" in an inline-entry input) where the cursor
   * may never enter the menu and the default auto-close prematurely
   * dismisses it.
   */
  persistent?: boolean;
}>();

const target = computed(() => {
  // just having something to trigger the computed
  __count.value;
  const target = props.target
    ? typeof props.target === "string"
      ? (document.querySelector(props.target) as HTMLElement)
      : (((props.target as any).$el || props.target) as HTMLElement)
    : (parent?.proxy?.$el as HTMLElement);

  if (target === proxy.$el) {
    return parent.proxy.$parent.$el;
  }
  return target;
});

const emit = defineEmits({
  "update:modelValue": (_value: boolean) => true,
  blur: (_e: FocusEvent) => true,
  focus: (_e: FocusEvent) => true,
});

provide("closeMenu", () => {
  show.value = false;
});

const menu = ref<HTMLElement | null>(null);
const content = ref<HTMLElement | null>(null);
const show = ref(props.modelValue ?? false);
const __count = ref(0);
const { proxy, parent } = getCurrentInstance()!;

const { position, updatePosition } = useMenuPosition(
  {
    offset: props.offset,
    maxHeight: props.maxHeight,
  },
  target,
  content,
  menu,
);

useEvent(target, "focusin", () => {
  if (props.noAutoOpen) return;
  show.value = true;
});

watch(show, async (s, _oldValue, onCleanup) => {
  emit("update:modelValue", s);
  await nextTick();

  if (content.value && !props.persistent) {
    content.value.addEventListener("mouseleave", handleMouseLeave);
  }

  onCleanup(() => {
    if (content.value) {
      content.value.removeEventListener("mouseleave", handleMouseLeave);
    }
  });
});

watch(
  menu,
  (v, _oldValue, onCleanup) => {
    if (!v) return;
    updatePosition();
    const id = window.setInterval(() => {
      updatePosition();
    }, 100);

    onCleanup(() => {
      window.clearInterval(id);
    });
  },
  { flush: "sync" },
);

watch(
  () => props.modelValue,
  (v, _oldValue, onCleanup) => {
    show.value = v;
    if (!v) return;
    updatePosition();
    const id = window.setInterval(() => {
      updatePosition();
    }, 100);

    onCleanup(() => {
      window.clearInterval(id);
    });
  },
);

const positionTop = computed(() => position.value.top);
const positionLeft = computed(() => position.value.left);
const positionWidth = computed(() => position.value.width);
const positionHeight = computed(() => position.value.height);

function handleMenuFocus() {
  if (
    document.activeElement === target.value ||
    document.activeElement === proxy?.$el ||
    (proxy?.$el as HTMLElement).contains(document.activeElement)
  ) {
    return;
  }
  dispatch("close-menus", { target: props.id });
  return (show.value = false);
}

function handleMouseLeave() {
  setTimeout(function () {
    show.value = false;
  }, 300);
}

onMounted(() => {
  // trigger target
  __count.value++;
  window.addEventListener("focus", handleMenuFocus, true);
  if (!props.persistent) {
    menu.value?.parentElement?.addEventListener("mouseleave", handleMouseLeave);
    menu.value?.parentElement?.addEventListener("scroll", handleMouseLeave);
  }
});

onUnmounted(() => {
  window.removeEventListener("focus", handleMenuFocus, true);
  content.value?.parentElement?.removeEventListener("mouseleave", handleMouseLeave);
  content.value?.parentElement?.removeEventListener("scroll", handleMouseLeave);
});

defineExpose({
  hide() {
    show.value = false;
  },
  show() {
    show.value = true;
  },
});
</script>
<template>
  <teleport v-if="renderTarget" :to="renderTarget">
    <div
      v-if="show"
      :id="id"
      ref="menu"
      class="m-menu"
      tabindex="-1"
      @focus="$emit('focus', $event)"
      @blur="$emit('blur', $event)"
    >
      <div ref="content" class="m-menu--content">
        <slot />
      </div>
    </div>
  </teleport>

  <div
    v-else-if="show"
    :id="id"
    ref="menu"
    class="m-menu"
    tabindex="-1"
    @focus="$emit('focus', $event)"
    @blur="$emit('blur', $event)"
  >
    <div ref="content" class="m-menu--content">
      <slot />
    </div>
  </div>
</template>

<style lang="scss">
.m-menu {
  animation: fadeIn 0.1s ease both;
  animation-delay: 50ms;

  position: fixed;
  z-index: 9002;

  @media (min-width: 505px) {
    top: v-bind(positionTop);
    left: v-bind(positionLeft);
    width: v-bind(positionWidth);
    max-height: v-bind(positionHeight);
  }
  will-change: auto;
  box-shadow:
    0px 4px 6px rgba(0, 0, 0, 0.06),
    0px 2px 4px rgba(0, 0, 0, 0.06);

  background-color: white;
  border-radius: 4px;
  overflow: hidden;

  display: flex;

  .m-menu--content {
    flex: 1 1 auto;
    overflow: auto;
  }
}
</style>
